import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Subject, catchError, firstValueFrom, of, switchMap, takeUntil } from 'rxjs';
import { MatTableModule } from '@angular/material/table';
import { MatTableDataSource } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CustomerStatsCardComponent } from '../../components/customer-stats-card/customer-stats-card.component';
import { PaymentsToolbarFiltersComponent } from '../components/payments-toolbar-filters/payments-toolbar-filters.component';
import { CustomerStatData } from '../../shared/types';
import { PaymentLedgerStatuses } from '../payment.data';
import {
  paymentsPeriodLabel,
  restorePaymentsPeriodSelection,
  type PaymentsPeriodFilter,
  type PaymentsPeriodSelection,
} from '../payments-period-filter.util';
import { DateRange } from '../../shared/components/date-range-picker/date-range-picker.component';
import { formatDateForPeriodApi } from '../../shared/utils/dashboard-period-filter.util';
import {
  DashboardTransactionItem,
  PaymentsDashboardService,
} from '../../services/payments-dashboard.service';
import {
  PaginationBarComponent,
  PaginationData,
} from '../../shared/components/pagination-bar/pagination-bar.component';
import { EmptyResultComponent } from '../../shared/components/empty-result/empty-result.component';
import {
  LoadingTableShimmerComponent,
  ShimmerColumn,
} from '../../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import { ProfileService } from '../../services/profile.service';
import { LocationService } from '../../services/location.service';
import {
  buildPaymentsLocationApiFilters,
  mapLocationsForPaymentsFilter,
  PAYMENTS_LOCATION_STORAGE_KEY,
  PaymentsLocationOption,
  resolvePaymentsLocationLabel,
} from '../payments-location-filter.util';
import { formatDateTimeInTimeZone } from '../../shared/utils/date.util';
import { OrderUtils } from '../../orders/utils/order.utils';
import { PaymentMethodCode } from '../../shared/types/payment-method.enum';

export interface PaymentLedgerRow {
  transactionId: string;
  dateTime: string;
  customerName: string;
  customerIdentifier: string;
  washType: string;
  washTypeClass: string;
  locationName: string;
  discountTerms: string;
  orderId: string;
  orderAmount: number;
  totalAmount: number;
  paymentMethod: string;
  status: string;
  statusClass: string;
  statusTooltip: string;
  convenienceFee: number;
  platformFee?: number | null;
}

const DASHBOARD_DATE_STORAGE_KEY = 'paymentsDashboardDateFilter';
const DASHBOARD_STATUS_STORAGE_KEY = 'paymentsDashboardStatusFilter';

interface PaymentStatCardMeta {
  title: string;
  gradient: string;
  icon: string;
  tooltip: string;
}

const PRIMARY_PAYMENT_STAT_CARDS: readonly PaymentStatCardMeta[] = [
  {
    title: 'Total Revenue',
    gradient: 'linear-gradient(180deg, #A9DF85 0%, #6DA548 100%)',
    icon: 'assets/dashboard/r-1.png',
    tooltip:
      'Card payments on paid orders (by appointment date, including convenience fees) plus membership renewals (by payment date). Fully refunded payments and membership plan-change charges are not included.',
  },
  {
    title: 'Net Earnings',
    gradient: 'linear-gradient(180deg, #AFA5F6 0%, #8878F2 100%)',
    icon: 'assets/dashboard/r-3.png',
    tooltip:
      'Total Revenue minus the platform fees and partial refunds on those same payments. Stripe processing fees are not deducted.',
  },
];

/** The one secondary card that reads Stripe on its own request (CWS-2012); the rest are DB-backed. */
const PENDING_PAYOUTS_STAT_CARD: PaymentStatCardMeta = {
  title: 'Pending Payouts',
  gradient: 'linear-gradient(180deg, #A3C7FF 0%, #5186D9 100%)',
  icon: 'assets/dashboard/r-2.png',
  tooltip:
    'Total dollar value of transactions that have been processed but not yet disbursed to the business bank account.',
};

const SECONDARY_PAYMENT_STAT_CARDS: readonly PaymentStatCardMeta[] = [
  PENDING_PAYOUTS_STAT_CARD,
  {
    title: 'Completed Payouts',
    gradient: 'linear-gradient(180deg, #85DF9C 0%, #48A55F 100%)',
    icon: 'assets/dashboard/c-1.png',
    tooltip: 'Cumulative total of all amounts successfully disbursed to the business bank account.',
  },
];

const REFUNDS_STAT_CARD_META: Omit<PaymentStatCardMeta, 'tooltip'> = {
  title: 'Refunds',
  gradient: 'linear-gradient(180deg, #8ED9F6 0%, #49A3D7 100%)',
  icon: 'assets/dashboard/r-4.png',
};

function refundsStatTooltip(
  refundTotal: number,
  refundCount: number,
  formatCurrency: (amount: number) => string
): string {
  return `${formatCurrency(refundTotal)} across ${refundCount} transaction${refundCount === 1 ? '' : 's'} (total dollar value and count of refunded transactions).`;
}

@Component({
  selector: 'app-payment-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatTableModule,
    MatTooltipModule,
    CustomerStatsCardComponent,
    PaymentsToolbarFiltersComponent,
    PaginationBarComponent,
    EmptyResultComponent,
    LoadingTableShimmerComponent,
  ],
  templateUrl: './payment-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './payment-dashboard.component.scss',
})
export class PaymentDashboardComponent implements OnInit, OnDestroy {
  readonly totalAmountHeaderTooltip =
    'Total amount charged to the customer (order amount + convenience fee).';

  ledgerStatuses = PaymentLedgerStatuses;
  selectedLedgerStatuses: string[] = [];
  selectedPeriod: PaymentsPeriodFilter = 'today';
  selectedPeriodLabel = paymentsPeriodLabel('today');
  customDateRange: DateRange = { startDate: null, endDate: null };
  ledgerRows: PaymentLedgerRow[] = [];

  // Material table properties
  dataSource = new MatTableDataSource<PaymentLedgerRow>();
  displayedColumns: string[] = [
    'transactionId',
    'dateTime',
    'customerName',
    'order',
    'washType',
    'locationName',
    'orderAmount',
    'convenienceFee',
    'totalAmount',
    'platformFee',
    'paymentMethod',
    'discount',
    'status',
  ];

  metricsLoading = true;
  /** CWS-2012: the Pending Payouts card waits on Stripe alone; the other cards render first. */
  pendingPayoutsLoading = true;
  /**
   * Stripe's live Pending Payouts figure for the current filters, or null until it lands (or when
   * it failed). The metrics mirror only fills the card while this is null: the two requests race,
   * and the metrics response must never overwrite a live figure that arrived first.
   */
  private livePendingPayouts: number | null = null;
  tableLoading = true;
  exportLoading = false;
  primaryMetricSkeletonItems = Array.from({ length: 2 });
  secondaryMetricSkeletonItems = Array.from({ length: 3 });
  shimmerColumns: ShimmerColumn[] = [
    { label: 'Transaction ID', width: 'md' },
    { label: 'Date & Time', width: 'md' },
    { label: 'Customer', width: 'md' },
    { label: 'Order', width: 'lg' },
    { label: 'Wash Type', width: 'sm' },
    { label: 'Location', width: 'md' },
    { label: 'Order Amount', width: 'sm' },
    { label: 'Convenience Fee', width: 'sm' },
    { label: 'Total Amount', width: 'sm' },
    { label: 'Platform Fee', width: 'sm' },
    { label: 'Payment Method', width: 'sm' },
    { label: 'Discount', width: 'md' },
    { label: 'Status', width: 'sm' },
  ];

  primaryPaymentStats: CustomerStatData[] = [];
  secondaryPaymentStats: CustomerStatData[] = [];
  locationOptions: PaymentsLocationOption[] = [];
  selectedLocationId = '';
  selectedLocationLabel = 'All Locations';

  totalRecords = 0;
  currentPage = 1;
  rows = 10;
  paginationData: PaginationData = {
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 10,
  };

  private metricsRequestToken = 0;
  private readonly destroy$ = new Subject<void>();
  private readonly transactionFetch$ = new Subject<{ resetPagination?: boolean }>();

  constructor(
    private readonly paymentsService: PaymentsDashboardService,
    private readonly profileService: ProfileService,
    private readonly locationService: LocationService
  ) {
    this.restoreFilters();
  }

  ngOnInit(): void {
    this.profileService.locations$.pipe(takeUntil(this.destroy$)).subscribe(locations => {
      this.locationOptions = mapLocationsForPaymentsFilter(locations);
      this.syncSelectedLocation();
    });
    this.profileService.loadLocations();
    this.setupTransactionStream();
    this.buildPaymentStats();
    this.fetchTransactions(true);
    this.fetchMetrics();
  }

  trackByStatTitle(_: number, stat: CustomerStatData): string {
    return stat.title;
  }

  /** Only the Pending Payouts card waits on Stripe; the other secondary cards never skeleton alone. */
  showPendingPayoutsSkeleton(stat: CustomerStatData): boolean {
    return this.pendingPayoutsLoading && stat.title === PENDING_PAYOUTS_STAT_CARD.title;
  }

  ngOnDestroy(): void {
    this.transactionFetch$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onPeriodChange(period: PaymentsPeriodFilter): void {
    this.selectedPeriod = period;
    this.selectedPeriodLabel = paymentsPeriodLabel(period);
    if (period !== 'Custom') {
      this.customDateRange = { startDate: null, endDate: null };
    }
    this.currentPage = 1;
    this.persistDateFilter();
    this.fetchTransactions();
    this.fetchMetrics();
  }

  onDateRangeApply(data: { period: string; customRange?: DateRange }): void {
    if (data.customRange) {
      this.customDateRange = {
        startDate: data.customRange.startDate ? new Date(data.customRange.startDate) : null,
        endDate: data.customRange.endDate ? new Date(data.customRange.endDate) : null,
      };
    } else if (data.period !== 'Custom') {
      this.customDateRange = { startDate: null, endDate: null };
    }
    this.onPeriodChange(data.period as PaymentsPeriodFilter);
  }

  private persistDateFilter(): void {
    const selection: PaymentsPeriodSelection = { period: this.selectedPeriod };
    if (
      this.selectedPeriod === 'Custom' &&
      this.customDateRange.startDate &&
      this.customDateRange.endDate
    ) {
      selection.startDate = formatDateForPeriodApi(this.customDateRange.startDate);
      selection.endDate = formatDateForPeriodApi(this.customDateRange.endDate);
    }
    localStorage.setItem(DASHBOARD_DATE_STORAGE_KEY, JSON.stringify(selection));
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.fetchTransactions();
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.rows = itemsPerPage;
    this.currentPage = 1;
    this.fetchTransactions(true);
  }

  onStatusChange(): void {
    this.currentPage = 1;
    localStorage.setItem(DASHBOARD_STATUS_STORAGE_KEY, JSON.stringify(this.selectedLedgerStatuses));
    this.fetchTransactions(true);
  }

  onLocationChange(locationId: string): void {
    this.selectedLocationId = locationId;
    this.selectedLocationLabel = resolvePaymentsLocationLabel(locationId, this.locationOptions);
    this.currentPage = 1;
    localStorage.setItem(PAYMENTS_LOCATION_STORAGE_KEY, locationId);
    this.syncShopLocationForPaymentsFilter(locationId);
    this.fetchTransactions(true);
    this.fetchMetrics();
  }

  async onExport(): Promise<void> {
    if (this.exportLoading) return;
    this.exportLoading = true;
    try {
      const blob = await firstValueFrom(
        this.paymentsService.exportDashboardTransactions({
          filters: this.buildTransactionFilters(),
        })
      );
      this.downloadBlob(blob, `terminal-transactions-${new Date().toISOString().slice(0, 10)}.csv`);
    } finally {
      this.exportLoading = false;
    }
  }

  private restoreFilters(): void {
    this.restoreStoredStatusFilter();
    this.restoreStoredDateFilter();
    this.restoreStoredLocationFilter();
    this.selectedLocationLabel = resolvePaymentsLocationLabel(
      this.selectedLocationId,
      this.locationOptions
    );
  }

  private restoreStoredStatusFilter(): void {
    try {
      const savedStatus = localStorage.getItem(DASHBOARD_STATUS_STORAGE_KEY);
      if (!savedStatus) return;
      const parsed = JSON.parse(savedStatus);
      if (Array.isArray(parsed)) {
        this.selectedLedgerStatuses = parsed.filter((s): s is string => typeof s === 'string');
      }
    } catch {
      this.selectedLedgerStatuses = [];
    }
  }

  private restoreStoredDateFilter(): void {
    try {
      const savedDate = localStorage.getItem(DASHBOARD_DATE_STORAGE_KEY);
      const selection = savedDate ? JSON.parse(savedDate) : undefined;
      const restored = restorePaymentsPeriodSelection(selection);
      this.selectedPeriod = restored.period;
      if (restored.period === 'Custom' && restored.startDate && restored.endDate) {
        this.customDateRange = {
          startDate: new Date(`${restored.startDate}T00:00:00`),
          endDate: new Date(`${restored.endDate}T00:00:00`),
        };
      } else {
        this.customDateRange = { startDate: null, endDate: null };
      }
    } catch {
      this.selectedPeriod = 'today';
      this.customDateRange = { startDate: null, endDate: null };
    }
    this.selectedPeriodLabel = paymentsPeriodLabel(this.selectedPeriod);
  }

  private restoreStoredLocationFilter(): void {
    const savedLocation = localStorage.getItem(PAYMENTS_LOCATION_STORAGE_KEY);
    this.selectedLocationId = savedLocation ?? '';
  }

  private syncSelectedLocation(): void {
    if (this.selectedLocationId && this.locationOptions.length) {
      const isValid = this.locationOptions.some(option => option.value === this.selectedLocationId);
      if (!isValid) {
        this.selectedLocationId = '';
        localStorage.setItem(PAYMENTS_LOCATION_STORAGE_KEY, '');
      }
    }
    this.selectedLocationLabel = resolvePaymentsLocationLabel(
      this.selectedLocationId,
      this.locationOptions
    );
    this.syncShopLocationForPaymentsFilter(this.selectedLocationId);
  }

  private syncShopLocationForPaymentsFilter(locationId: string): void {
    if (!locationId) {
      this.locationService.clearShopLocation();
      return;
    }
    this.locationService.setShopLocationById(locationId);
  }

  private setupTransactionStream(): void {
    this.transactionFetch$
      .pipe(
        switchMap(({ resetPagination = false }) => {
          this.tableLoading = true;
          if (resetPagination) {
            this.resetPaginationState();
          }
          this.ledgerRows = [];
          this.dataSource.data = [];
          return this.paymentsService
            .getDashboardTransactions({
              paging: { page: this.currentPage, limit: this.rows },
              filters: this.buildTransactionFilters(),
            })
            .pipe(catchError(() => of(null)));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: data => {
          if (!data) {
            this.ledgerRows = [];
            this.dataSource.data = [];
            this.tableLoading = false;
            return;
          }
          this.ledgerRows = (data.items ?? []).map(item => this.mapToLedgerRow(item));
          this.dataSource.data = this.ledgerRows;
          this.totalRecords = data.pagination?.total ?? 0;
          this.paginationData = {
            currentPage: data.pagination?.page ?? this.currentPage,
            totalPages: data.pagination?.totalPages ?? 0,
            totalItems: this.totalRecords,
            itemsPerPage: data.pagination?.limit ?? this.rows,
          };
          this.tableLoading = false;
        },
      });
  }

  private fetchTransactions(resetPagination = false): void {
    this.transactionFetch$.next({ resetPagination });
  }

  private resetPaginationState(): void {
    this.totalRecords = 0;
    this.paginationData = {
      currentPage: this.currentPage,
      totalPages: 0,
      totalItems: 0,
      itemsPerPage: this.rows,
    };
  }

  private fetchMetrics(): void {
    const requestToken = ++this.metricsRequestToken;
    this.metricsLoading = true;
    this.livePendingPayouts = null;
    this.fetchPendingPayouts(requestToken);
    this.paymentsService
      .getDashboardMetrics({
        filters: this.buildMetricsFilters(),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: metrics => {
          if (requestToken !== this.metricsRequestToken) return;
          this.buildPaymentStats({
            totalRevenue: metrics?.totalRevenue ?? 0,
            netEarnings: metrics?.netEarnings ?? 0,
            pendingPayouts: metrics?.pendingPayouts ?? 0,
            completedPayouts: metrics?.completedPayouts ?? 0,
            refundTotal: metrics?.refunds?.total ?? 0,
            refundCount: metrics?.refunds?.count ?? 0,
          });
          this.metricsLoading = false;
        },
        error: () => {
          if (requestToken !== this.metricsRequestToken) return;
          this.buildPaymentStats();
          this.metricsLoading = false;
        },
      });
  }

  /**
   * The Pending Payouts figure on its own request: Stripe's live balance can take seconds,
   * and the four DB-backed cards should not wait for it. Until it lands the card is a
   * skeleton; on an error it keeps the metrics (mirror) figure it was built with.
   */
  private fetchPendingPayouts(requestToken: number): void {
    this.pendingPayoutsLoading = true;
    this.paymentsService
      .getPendingPayouts({ filters: this.buildMetricsFilters() })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          if (requestToken !== this.metricsRequestToken) return;
          if (typeof result?.pendingPayouts === 'number') {
            this.livePendingPayouts = result.pendingPayouts;
            this.setPendingPayoutsStat(result.pendingPayouts);
          }
          this.pendingPayoutsLoading = false;
        },
        error: () => {
          if (requestToken !== this.metricsRequestToken) return;
          this.pendingPayoutsLoading = false;
        },
      });
  }

  private setPendingPayoutsStat(pendingPayouts: number): void {
    const liveCard = this.toStatCard(
      PENDING_PAYOUTS_STAT_CARD,
      this.formatStatCurrency(pendingPayouts)
    );
    this.secondaryPaymentStats = this.secondaryPaymentStats.map(stat =>
      stat.title === PENDING_PAYOUTS_STAT_CARD.title ? liveCard : stat
    );
  }

  /**
   * The terms this invoice applied — "100% off", "$20.00 off".
   *
   * Deliberately the discount's TERMS rather than the dollars it removed: on an advance_custom
   * ladder the stored 50% bills its renewals at 100%, and the backend already recomputes the real
   * figure per invoice. Empty when the order carried no discount.
   */
  private formatDiscountTerms(item: DashboardTransactionItem): string {
    const value = item.discountValue;
    if (value === null || value === undefined) return '-';
    if ((item.discountType ?? '').toLowerCase() === 'percentage') {
      return `${Number.isInteger(value) ? value : Number(value.toFixed(2))}% off`;
    }
    return `$${Number(value).toFixed(2)} off`;
  }

  private mapToLedgerRow(item: DashboardTransactionItem): PaymentLedgerRow {
    const timezone = this.locationService.getTimezoneForLocationId(this.selectedLocationId);
    const dateTime = item.paymentDate ? formatDateTimeInTimeZone(item.paymentDate, timezone) : '';

    const totalRefunded = item.refunds?.reduce((sum, r) => sum + (r.amount ?? 0), 0) ?? 0;
    let statusTooltip = '';
    if (item.status === 'refunded') {
      statusTooltip = 'This payment was fully refunded.';
    } else if (item.status === 'partially_refunded') {
      statusTooltip = `Partially refunded: $${totalRefunded.toFixed(2)}`;
    }

    const orderAmount = item.baseAmount ?? 0;
    const convenienceFee = item.convenienceFee ?? 0;

    return {
      // No Stripe reference means no transaction id. Falling back to `item.id` printed the
      // internal payments row number in a column that is meant to be a Stripe handle.
      transactionId: item.transactionId || '-',
      dateTime,
      customerName: item.customerName?.trim() || '-',
      customerIdentifier: item.customerIdentifier,
      washType: this.formatWashType(item.washType || ''),
      washTypeClass: this.getWashTypeClass(item.washType || ''),
      locationName: item.locationName || '-',
      discountTerms: this.formatDiscountTerms(item),
      orderId: item.orderId,
      orderAmount,
      totalAmount: orderAmount + convenienceFee,
      paymentMethod: OrderUtils.formatPaymentMethod(
        item.paymentMethodType?.trim() ||
          (item.cardLast4?.trim() ? PaymentMethodCode.Card : undefined)
      ),
      status: this.formatStatus(item.status),
      statusClass: item.status,
      statusTooltip,
      convenienceFee,
      platformFee: item.platformFee ?? null,
    };
  }

  private formatStatus(status: string): string {
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private formatWashType(washType: string): string {
    switch (washType) {
      case 'single_wash':
        return 'Single Wash';
      case 'monthly_membership':
        return 'Monthly Membership';
      default:
        return washType ? washType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '-';
    }
  }

  private getWashTypeClass(washType: string): string {
    switch (washType) {
      case 'monthly_membership':
        return 'wash-type-purple';
      case 'single_wash':
        return 'wash-type-orange';
      default:
        return '';
    }
  }

  private buildDateFilters(): {
    filterBy: string;
    startDate?: string;
    endDate?: string;
    timezone: string;
  } {
    const timezone = this.locationService.getTimezoneForLocationId(this.selectedLocationId);
    if (
      this.selectedPeriod === 'Custom' &&
      this.customDateRange.startDate &&
      this.customDateRange.endDate
    ) {
      return {
        filterBy: 'custom',
        startDate: formatDateForPeriodApi(this.customDateRange.startDate),
        endDate: formatDateForPeriodApi(this.customDateRange.endDate),
        timezone,
      };
    }
    return {
      filterBy: this.selectedPeriod,
      timezone,
    };
  }

  private buildBaseDashboardFilters(): {
    filterBy?: string;
    startDate?: string;
    endDate?: string;
    locationId?: string;
    stripeAccountId?: string;
    timezone?: string;
  } {
    return {
      ...this.buildDateFilters(),
      ...buildPaymentsLocationApiFilters(this.selectedLocationId, this.locationOptions),
    };
  }

  private buildMetricsFilters(): {
    filterBy?: string;
    startDate?: string;
    endDate?: string;
    locationId?: string;
    stripeAccountId?: string;
    timezone?: string;
  } {
    return this.buildBaseDashboardFilters();
  }

  private buildTransactionFilters(): {
    filterBy?: string;
    startDate?: string;
    endDate?: string;
    status?: string[];
    locationId?: string;
    stripeAccountId?: string;
    timezone?: string;
  } {
    return {
      ...this.buildBaseDashboardFilters(),
      ...(this.selectedLedgerStatuses.length > 0 && { status: this.selectedLedgerStatuses }),
    };
  }

  private buildPaymentStats(metrics?: {
    totalRevenue: number;
    netEarnings: number;
    pendingPayouts: number;
    completedPayouts: number;
    refundTotal: number;
    refundCount: number;
  }): void {
    const totalRevenue = metrics?.totalRevenue ?? 0;
    const netEarnings = metrics?.netEarnings ?? 0;
    const pendingPayouts = this.livePendingPayouts ?? metrics?.pendingPayouts ?? 0;
    const completedPayouts = metrics?.completedPayouts ?? 0;
    const refundTotal = metrics?.refundTotal ?? 0;
    const refundCount = metrics?.refundCount ?? 0;

    this.primaryPaymentStats = [
      this.toStatCard(PRIMARY_PAYMENT_STAT_CARDS[0], this.formatStatCurrency(totalRevenue)),
      this.toStatCard(PRIMARY_PAYMENT_STAT_CARDS[1], this.formatStatCurrency(netEarnings)),
    ];

    this.secondaryPaymentStats = [
      this.toStatCard(PENDING_PAYOUTS_STAT_CARD, this.formatStatCurrency(pendingPayouts)),
      this.toStatCard(SECONDARY_PAYMENT_STAT_CARDS[1], this.formatStatCurrency(completedPayouts)),
      this.toStatCard(
        {
          ...REFUNDS_STAT_CARD_META,
          tooltip: refundsStatTooltip(refundTotal, refundCount, amount =>
            this.formatTooltipCurrency(amount)
          ),
        },
        this.formatStatCurrency(refundTotal),
        refundCount > 0 ? `${refundCount} refunded payment${refundCount === 1 ? '' : 's'}` : ''
      ),
    ];
  }

  private toStatCard(meta: PaymentStatCardMeta, value: string, periodText = ''): CustomerStatData {
    return {
      title: meta.title,
      value,
      percentage: '',
      periodText,
      gradient: meta.gradient,
      icon: meta.icon,
      tooltip: meta.tooltip,
    };
  }

  private formatStatCurrency(amount: number): string {
    const value = Number(amount ?? 0);
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  private formatTooltipCurrency(amount: number): string {
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
